import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Icon,
  InitialsAvatar,
  Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbUser, TbUsers } from "react-icons/tb";

import { convertStringToUserStatus } from "@/constants/user-status";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useMe } from "@/hooks/queries/user.queries";
import { cn } from "@/lib/utils";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { USER_STATUS_LABELS, UserStatus } from "@/types/user-statuses.types";
import { fadeInUp, transitions } from "@/utils/animations";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { getStatusCircleColor } from "@/utils/user-status";

import { ProfileInfo } from "./components/profile-info";
import { TeamsList } from "./components/profile-teams-list";

export const Route = createFileRoute(APP_ROUTES_FULL.PROFILE)({
  component: Profile,
});

function Profile() {
  const { t } = useTranslation();
  const { data: user, isLoading: isLoadingUser, error: userError } = useMe();
  const {
    data: teamsResponse = { data: [] },
    isLoading: isLoadingTeams,
    error: teamsError,
  } = useTeams();
  const { status: userStatus } = useUserStatusContext();

  const teams = teamsResponse.data;

  const hasUserError = userError || !user;

  const userStatusColor = useMemo(() => {
    if (!userStatus) return getStatusCircleColor(UserStatus.OFFLINE);

    const enumStatus =
      convertStringToUserStatus(userStatus) || UserStatus.OFFLINE;
    return getStatusCircleColor(enumStatus);
  }, [userStatus]);

  if (isLoadingUser) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-background">
        <Card className="w-full max-w-md flex items-center justify-center min-h-[300px]">
          <Spinner size="lg" />
        </Card>
      </div>
    );
  }

  if (hasUserError) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-background">
        <Card className="w-full max-w-md flex items-center justify-center min-h-[300px]">
          <div className="text-destructive text-center w-full">
            {t("error")}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-background via-background to-muted/30">
      <div className="container mx-auto p-2 max-w-6xl">
        <motion.div
          {...fadeInUp}
          transition={transitions.slow}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/5 via-primary/3 to-background border border-primary/10 shadow-sm">
            <div className="relative p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="relative">
                  <InitialsAvatar
                    name={user.name}
                    size="lg"
                    className="w-24 h-24 border-4 shadow-xl"
                  />
                  <div
                    className={cn(
                      "absolute -bottom-1 -right-1 w-8 h-8 rounded-full border-4 border-background shadow-xl",
                      userStatusColor,
                    )}
                  />
                </div>
                <div className="flex-1 text-center md:text-left space-y-4">
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                      {user.name}
                    </h1>
                    <p className="text-xl text-muted-foreground mt-2">
                      {user.email}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <Badge
                      label={
                        userStatus
                          ? USER_STATUS_LABELS[userStatus]
                          : t("unknown")
                      }
                      variant="outline"
                      size="md"
                    />
                    <Badge
                      label={`${teams.length} ${t("teams")}`}
                      variant="outline"
                      size="md"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Personal Information */}
              <Card className="border-border/40 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon icon={TbUser} size={20} className="text-primary" />
                    </div>
                    <h2 className="text-2xl font-semibold">
                      {t("personal_information")}
                    </h2>
                  </div>
                </CardHeader>
                <CardContent>
                  <ProfileInfo user={user} />
                </CardContent>
              </Card>

              {/* Teams Section */}
              <Card className="border-border/40 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon
                          icon={TbUsers}
                          size={20}
                          className="text-primary"
                        />
                      </div>
                      <h2 className="text-2xl font-semibold">
                        {t("my_teams")}
                      </h2>
                    </div>
                    <Badge
                      label={`${teams.length} ${t("teams")}`}
                      variant="outline"
                      size="md"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <TeamsList
                      teams={teams}
                      isLoading={isLoadingTeams}
                      error={teamsError}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aside */}
            <aside className="space-y-8">
              {/* Account Info */}
              <Card className="border-border/40 shadow-sm">
                <CardHeader>
                  <CardTitle>{t("account_information")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {t("member_since")}
                    </span>
                    <span className="text-sm font-medium">
                      {user.createdAt
                        ? DateTime.fromISO(user.createdAt).toLocaleString(
                            DateTime.DATE_MED,
                          )
                        : "—"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
